import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import api, { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";
import {
  Plus,
  Trash,
  PencilSimple,
  CaretLeft,
  CaretRight,
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const LANG_MAP = { es: "es-ES", pt: "pt-BR" };

function formatDateISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getMonthGrid(year, month) {
  // returns 6x7 grid of Date objects
  const first = new Date(year, month, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday = 0
  const start = new Date(year, month, 1 - startDow);
  const days = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days;
}

export default function Agenda() {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useAuth();
  const locale = LANG_MAP[i18n.language] || "es-ES";

  const [items, setItems] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);

  const [viewDate, setViewDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null); // Date
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editing, setEditing] = useState(null);

  const empty = {
    funcionario_ids: [],
    cliente_id: "",
    data: formatDateISO(new Date()),
    horario_inicio: "08:00",
    horario_fim: "17:00",
    descricao: "",
    status: "planejado",
  };
  const [form, setForm] = useState(empty);
  const [multiDay, setMultiDay] = useState(false);
  const [multiDates, setMultiDates] = useState([]); // array of YYYY-MM-DD

  const load = async () => {
    setLoading(true);
    try {
      const [a, c] = await Promise.all([api.get("/agenda"), api.get("/clientes")]);
      setItems(a.data);
      setClientes(c.data);
      if (isAdmin) {
        const f = await api.get("/funcionarios");
        setFuncionarios(f.data.filter((u) => u.role === "funcionario"));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const grid = useMemo(() => getMonthGrid(year, month), [year, month]);

  const itemsByDay = useMemo(() => {
    const m = {};
    items.forEach((it) => {
      if (!m[it.data]) m[it.data] = [];
      m[it.data].push(it);
    });
    // sort each day by start time
    Object.values(m).forEach((arr) => arr.sort((a, b) => a.horario_inicio.localeCompare(b.horario_inicio)));
    return m;
  }, [items]);

  const monthLabel = new Date(year, month, 1).toLocaleDateString(locale, { month: "long", year: "numeric" });
  const weekDays = useMemo(() => {
    const d = new Date(2024, 0, 1); // Mon
    const names = [];
    for (let i = 0; i < 7; i++) {
      const x = new Date(d);
      x.setDate(d.getDate() + i);
      names.push(x.toLocaleDateString(locale, { weekday: "short" }));
    }
    return names;
  }, [locale]);

  const today = formatDateISO(new Date());

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => {
    const now = new Date();
    setViewDate(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDay(now);
  };

  const getClientName = (cid) => clientes.find((c) => c.id === cid)?.nome || "—";
  const getClientAddress = (cid) => clientes.find((c) => c.id === cid)?.endereco || "";
  const getFuncName = (fid) => funcionarios.find((f) => f.id === fid)?.name || "—";
  const getFuncNames = (ids) => (ids || []).map(getFuncName).filter((n) => n !== "—").join(", ") || "—";
  const toggleFunc = (fid) => {
    setForm((f) => {
      const has = f.funcionario_ids.includes(fid);
      return { ...f, funcionario_ids: has ? f.funcionario_ids.filter((x) => x !== fid) : [...f.funcionario_ids, fid] };
    });
  };

  const openAdd = () => {
    if (!selectedDay) return;
    setEditing(null);
    setForm({ ...empty, data: formatDateISO(selectedDay) });
    setShowFormDialog(true);
  };

  const openEdit = (item) => {
    setEditing(item.id);
    setForm({
      funcionario_ids: item.funcionario_ids || (item.funcionario_id ? [item.funcionario_id] : []),
      cliente_id: item.cliente_id,
      data: item.data,
      horario_inicio: item.horario_inicio,
      horario_fim: item.horario_fim,
      descricao: item.descricao || "",
      status: item.status,
    });
    setShowFormDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/agenda/${editing}`, form);
        toast.success(t("common.success"));
      } else if (multiDay && multiDates.length > 0) {
        const res = await api.post("/agenda/bulk", { ...form, datas: multiDates });
        toast.success(`${res.data.count} ${t("agenda.eventsCreated")}`);
      } else {
        await api.post("/agenda", form);
        toast.success(t("common.success"));
      }
      setShowFormDialog(false);
      setEditing(null);
      setMultiDay(false);
      setMultiDates([]);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const toggleMultiDate = (iso) => {
    setMultiDates((prev) => prev.includes(iso) ? prev.filter((x) => x !== iso) : [...prev, iso].sort());
  };

  const handleDelete = async (id) => {
    if (!window.confirm("?")) return;
    try {
      await api.delete(`/agenda/${id}`);
      toast.success(t("common.success"));
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const selectedKey = selectedDay ? formatDateISO(selectedDay) : null;
  const dayItems = selectedKey ? itemsByDay[selectedKey] || [] : [];

  return (
    <div data-testid="agenda-page">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl heading-font font-bold text-slate-900">{t("agenda.title")}</h1>
          <p className="text-slate-500 mt-1 text-sm">{t("agenda.subtitle")}</p>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={goToday} data-testid="cal-today-btn">
            <CalendarIcon size={16} className="mr-1" /> {new Date().toLocaleDateString(locale, { day: "numeric", month: "short" })}
          </Button>
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="cal-prev-btn">
            <CaretLeft size={18} />
          </Button>
          <div className="px-4 py-2 heading-font font-semibold text-slate-900 min-w-[180px] text-center capitalize">
            {monthLabel}
          </div>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="cal-next-btn">
            <CaretRight size={18} />
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-400">{t("common.loading")}</Card>
      ) : (
        <Card className="border-slate-200 overflow-hidden">
          {/* Week header */}
          <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
            {weekDays.map((wd, i) => (
              <div
                key={i}
                className="px-2 py-3 text-[11px] font-bold uppercase tracking-wider text-slate-500 text-center"
              >
                {wd}
              </div>
            ))}
          </div>

          {/* Days grid */}
          <div className="grid grid-cols-7">
            {grid.map((d, idx) => {
              const key = formatDateISO(d);
              const inMonth = d.getMonth() === month;
              const isToday = key === today;
              const isSelected = selectedKey === key;
              const dayEvents = itemsByDay[key] || [];

              return (
                <button
                  type="button"
                  key={idx}
                  onClick={() => setSelectedDay(d)}
                  data-testid={`cal-day-${key}`}
                  className={`
                    group relative min-h-[100px] sm:min-h-[120px] text-left p-2 border-r border-b border-slate-100
                    transition-colors
                    ${inMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/60 text-slate-400"}
                    ${isSelected ? "ring-2 ring-green-500 ring-inset z-10" : ""}
                    ${(idx + 1) % 7 === 0 ? "border-r-0" : ""}
                    ${idx >= 35 ? "border-b-0" : ""}
                  `}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className={`
                        inline-flex items-center justify-center w-7 h-7 text-sm font-semibold rounded-full
                        ${isToday ? "bg-red-600 text-white" : inMonth ? "text-slate-900" : "text-slate-400"}
                      `}
                    >
                      {d.getDate()}
                    </span>
                    {dayEvents.length > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-green-700 bg-green-50 px-1.5 py-0.5 rounded">
                        {dayEvents.length}
                      </span>
                    )}
                  </div>

                  <div className="space-y-1">
                    {dayEvents.slice(0, 2).map((ev) => {
                      const names = getFuncNames(ev.funcionario_ids);
                      return (
                        <div
                          key={ev.id}
                          className={`text-[10px] px-1.5 py-0.5 rounded border-l-2 ${
                            ev.status === "concluido"
                              ? "bg-green-50 border-green-600 text-green-800"
                              : "bg-amber-50 border-amber-500 text-amber-800"
                          }`}
                          title={`${ev.horario_inicio}—${ev.horario_fim} · ${names} · ${getClientName(ev.cliente_id)} · ${getClientAddress(ev.cliente_id)}`}
                        >
                          <div className="font-semibold truncate">
                            {ev.horario_inicio} {names}
                          </div>
                          {getClientAddress(ev.cliente_id) && (
                            <div className="truncate opacity-80">{getClientAddress(ev.cliente_id)}</div>
                          )}
                        </div>
                      );
                    })}
                    {dayEvents.length > 2 && (
                      <div className="text-[10px] text-slate-500 px-1 font-medium">
                        + {dayEvents.length - 2}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Day details Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={(o) => !o && setSelectedDay(null)}>
        <DialogContent className="max-w-2xl" data-testid="day-details-dialog">
          <DialogHeader>
            <DialogTitle className="capitalize heading-font">
              {selectedDay?.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 max-h-[60vh] overflow-y-auto">
            {dayItems.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <CalendarIcon size={36} weight="duotone" className="mx-auto mb-2 text-slate-300" />
                <p className="text-sm">{t("agenda.empty")}</p>
              </div>
            ) : (
              dayItems.map((it) => (
                <div
                  key={it.id}
                  className="p-4 border border-slate-200 rounded-lg hover:border-slate-300 transition-colors"
                  data-testid={`day-event-${it.id}`}
                >
                  <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-lg bg-green-50 text-green-700 flex items-center justify-center">
                        <Clock size={18} weight="duotone" />
                      </div>
                      <div>
                        <div className="heading-font font-semibold text-slate-900">
                          {it.horario_inicio} — {it.horario_fim}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-1">
                          <Users size={12} weight="duotone" /> {getFuncNames(it.funcionario_ids)}
                        </div>
                      </div>
                    </div>
                    <Badge
                      className={
                        it.status === "concluido"
                          ? "bg-green-100 text-green-800 border-0"
                          : "bg-amber-100 text-amber-800 border-0"
                      }
                    >
                      {it.status === "concluido" ? t("agenda.done") : t("agenda.planned")}
                    </Badge>
                  </div>

                  <div className="text-sm space-y-1 pl-11">
                    <div>
                      <span className="text-slate-500">{t("agenda.client")}: </span>
                      <span className="font-medium text-slate-800">{getClientName(it.cliente_id)}</span>
                    </div>
                    {getClientAddress(it.cliente_id) && (
                      <div className="flex items-start gap-1.5">
                        <MapPin size={14} weight="duotone" className="text-red-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{getClientAddress(it.cliente_id)}</span>
                      </div>
                    )}
                    {it.descricao && <div className="text-slate-600">{it.descricao}</div>}
                  </div>

                  {isAdmin && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 pl-11">
                      <Button size="sm" variant="outline" onClick={() => openEdit(it)} data-testid={`edit-day-event-${it.id}`}>
                        <PencilSimple size={14} className="mr-1" /> {t("agenda.edit")}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(it.id)}
                        data-testid={`delete-day-event-${it.id}`}
                      >
                        <Trash size={14} />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {isAdmin && (
            <DialogFooter>
              <Button onClick={openAdd} className="bg-green-600 hover:bg-green-700 gap-2" data-testid="day-add-btn">
                <Plus size={16} weight="bold" /> {t("agenda.add")}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog
        open={showFormDialog}
        onOpenChange={(o) => {
          setShowFormDialog(o);
          if (!o) setEditing(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? t("agenda.edit") : t("agenda.add")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editing && (
              <div className="flex items-center gap-2 p-2 bg-slate-50 rounded border border-slate-200">
                <Checkbox
                  checked={multiDay}
                  onCheckedChange={(v) => { setMultiDay(v); if (!v) setMultiDates([]); }}
                  data-testid="multi-day-toggle"
                />
                <Label className="cursor-pointer text-sm">{t("agenda.multiDay")}</Label>
              </div>
            )}

            {multiDay && !editing ? (
              <div>
                <Label>{t("agenda.selectDates")}</Label>
                <Input
                  type="date"
                  onChange={(e) => { if (e.target.value) { toggleMultiDate(e.target.value); e.target.value = ""; } }}
                  data-testid="multi-date-input"
                />
                {multiDates.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {multiDates.map((d) => (
                      <Badge
                        key={d}
                        className="bg-green-100 text-green-800 border-0 cursor-pointer"
                        onClick={() => toggleMultiDate(d)}
                        data-testid={`selected-date-${d}`}
                      >
                        {d} ✕
                      </Badge>
                    ))}
                  </div>
                )}
                <p className="text-xs text-slate-500 mt-1">{multiDates.length} {t("agenda.datesSelected")}</p>
              </div>
            ) : (
              <div>
                <Label>{t("agenda.date")}</Label>
                <Input
                  type="date"
                  value={form.data}
                  onChange={(e) => setForm({ ...form, data: e.target.value })}
                  required
                  data-testid="agenda-date-input"
                />
              </div>
            )}
            <div>
              <Label>{t("agenda.employee")}</Label>
              <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto bg-white">
                {funcionarios.length === 0 && (
                  <div className="text-sm text-slate-400 py-1">—</div>
                )}
                {funcionarios.map((f) => {
                  const checked = form.funcionario_ids.includes(f.id);
                  return (
                    <label
                      key={f.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 rounded px-1 py-1"
                      data-testid={`agenda-func-${f.id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleFunc(f.id)}
                      />
                      <span className="text-sm text-slate-800">{f.name}</span>
                    </label>
                  );
                })}
              </div>
              {form.funcionario_ids.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  {form.funcionario_ids.length} {form.funcionario_ids.length === 1 ? "empleado" : "empleados"}
                </p>
              )}
            </div>
            <div>
              <Label>{t("agenda.client")}</Label>
              <Select value={form.cliente_id} onValueChange={(v) => setForm({ ...form, cliente_id: v })}>
                <SelectTrigger data-testid="agenda-client-select">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  {clientes.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t("agenda.start")}</Label>
                <Input
                  type="time"
                  value={form.horario_inicio}
                  onChange={(e) => setForm({ ...form, horario_inicio: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label>{t("agenda.end")}</Label>
                <Input
                  type="time"
                  value={form.horario_fim}
                  onChange={(e) => setForm({ ...form, horario_fim: e.target.value })}
                  required
                />
              </div>
            </div>
            <div>
              <Label>{t("agenda.description")}</Label>
              <Textarea
                value={form.descricao}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label>{t("agenda.status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="planejado">{t("agenda.planned")}</SelectItem>
                  <SelectItem value="concluido">{t("agenda.done")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowFormDialog(false)}>
                {t("agenda.cancel")}
              </Button>
              <Button type="submit" className="bg-green-600 hover:bg-green-700" data-testid="agenda-save-btn">
                {t("agenda.save")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
